import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Dropdown from "@shared/v2/Dropdown";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import { Option } from "@shared/v2/Dropdown/custom";
import Avatar from "@shared/v2/Avatar";
import Checkbox from "@shared/v2/Checkbox";

const CustomOption = ({ children, componentProps, data }) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Option preserveStyle {...componentProps}>
    <div className="tw-flex tw-gap-[8px] tw-items-center">
      <Checkbox readOnly checked={componentProps.isSelected} />
      <Avatar alt={data.label} src={data.meta.avatarUrl} />
      {children}
    </div>
  </Option>
);

CustomOption.propTypes = {
  children: PropTypes.node.isRequired,
  componentProps: PropTypes.shape().isRequired,
  data: PropTypes.shape().isRequired,
};

const PondOrAgentFilter = ({
  enableLeadPonds = false,
  selectedAgentsFilters = [],
  selectedPondsFilters = [],
}) => {
  const [firstFocus, setFirstFocus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [assignableAgents, setAssignableAgents] = useState([]);
  const [assignablePonds, setAssignablePonds] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState(
    selectedAgentsFilters?.map((a) => ({
      label: a.name,
      value: String(a.id),
      meta: { isAgent: true, avatarUrl: a.imageSrc },
    })),
  );
  const [selectedPonds, setSelectedPonds] = useState(
    selectedPondsFilters?.ids.map((id, i) => ({
      label: selectedPondsFilters.names[i],
      value: id,
      meta: { isPond: true },
    })),
  );
  const value = useMemo(
    () => [...(selectedAgents || []), ...(selectedPonds || [])],
    [selectedAgents, selectedPonds],
  );
  const options = useMemo(() => {
    const ponds = assignablePonds.length ? [{ label: "Pond", options: assignablePonds }] : [];
    return [...ponds, { label: "Team", options: assignableAgents }];
  }, [assignableAgents, assignablePonds]);

  useEffect(() => {
    const clearAllButton = document.querySelector("#clear-all-modal-filters");
    if (!clearAllButton) return () => {};
    const clearFilters = () => {
      setSelectedAgents([]);
      setSelectedPonds([]);
    };
    clearAllButton.addEventListener("click", clearFilters);
    return () => clearAllButton.removeEventListener("click", clearFilters);
  }, []);

  useEffect(() => {
    Brivity.ClearAllFiltersButton?.toggle();
  }, [selectedAgents, selectedPonds]);

  useEffect(() => {
    if (!firstFocus) return;
    setFirstFocus(true);
    setLoading(true);
    const promises = [
      caseTransformingAxios
        .get("/account_active_agents")
        .then(({ data }) =>
          data.map((a) => ({ label: a.name, value: String(a.id), meta: { isAgent: true } })),
        ),
    ];
    if (enableLeadPonds) {
      promises.push(
        caseTransformingAxios
          .get("/lead_ponds.json?per_page=1000&page=1")
          .then(({ data }) => data.page.map((p) => ({ label: p.name, value: p.id, meta: { isPond: true } }))),
      );
    }
    Promise.all(promises).then(([agents, ponds]) => {
      setAssignableAgents(agents);
      if (ponds) setAssignablePonds(ponds);
      setLoading(false);
    });
  }, [firstFocus]);

  return (
    <div className="pond-or-agent-filter tw-relative lp-filter">
      <Dropdown
        blurInputOnSelect={false}
        closeMenuOnSelect={false}
        components={{ Option: CustomOption }}
        hideSelectedOptions={false}
        isLoading={loading}
        isMulti
        isSearchable
        filterOption={() => false}
        label={enableLeadPonds ? "Assigned To" : "Primary Agent"}
        menuShouldComeToFront
        onChange={(opts) => {
          const agentOptions = opts.filter((o) => o.meta.isAgent);
          const pondOptions = opts.filter((o) => o.meta.isPond);
          setSelectedAgents(agentOptions);
          setSelectedPonds(pondOptions);
        }}
        onFocus={() => setFirstFocus(true)}
        options={options}
        placeholder={enableLeadPonds ? "Select primary agents or lead ponds" : "Search to select"}
        value={value}
      />

      {selectedPonds?.map((p) => (
        <span key={p.value}>
          <input type="hidden" name="leads_filter[selected_lead_ponds][names][]" value={p.label} />
          <input type="hidden" name="leads_filter[selected_lead_ponds][ids][]" value={p.value} />
        </span>
      ))}

      {selectedAgents?.map((a) => (
        <input key={a.value} type="hidden" name="leads_filter[selected_primary_agents][]" value={a.value} />
      ))}
    </div>
  );
};

PondOrAgentFilter.propTypes = {
  enableLeadPonds: PropTypes.bool,
  selectedAgentsFilters: PropTypes.arrayOf(PropTypes.shape()),
  selectedPondsFilters: PropTypes.arrayOf(PropTypes.shape()),
};

PondOrAgentFilter.defaultProps = {
  enableLeadPonds: false,
  selectedAgentsFilters: [],
  selectedPondsFilters: [],
};

export default PondOrAgentFilter;
